import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import generateId from './generateId';
import { updateHashProps, rewriteUserId } from '../general';
import { addDatasToSave, loadDbModel } from './workBackendSlice';
import { saveAllData, saveData, deleteData, SignUpPayload } from './dataBackend';

export type SheetKinds = 'story' | 'map' | 'grid';

export type SheetDb = {
  name: string;
  sheetKind: SheetKinds;
  goodsId?: string;
  version?: number;
}
const initial: SheetDb = {
  name: '',
  sheetKind: 'map',
  goodsId: undefined,
  version: undefined
};

export type SheetDbHashType = {
  [key in string]: SheetDb;
}

export type SheetAddPayload = {
  id?: string;
} & SheetDb;
export type SheetUpdatePayload = {
  id: string;
} & { [P in keyof SheetDb]?: SheetDb[P] };

export type SheetDbState = {
  sheets: SheetDbHashType;
  currentSheetId: string | null;
}

export const  generateSheetId = () : string =>  generateId('s');
let initialState: SheetDbState = { sheets: {}, currentSheetId: null };
const key = 'sheets';

const sheetDbSlice = createSlice({
  name: 'sheetDb',
  initialState,
  reducers: {
    initializeSheets(state: SheetDbState) {
      state.sheets = {};
    },
    addSheet(state: SheetDbState, action: PayloadAction<SheetAddPayload>) {
      let { id, ...obj } = action.payload;
      if (!id) id = generateSheetId();
      state.sheets[id] = obj;
      saveData(key, id, obj);
    },
    updateSheet(state: SheetDbState, action: PayloadAction<SheetUpdatePayload>) {
      const { id, ...obj } = action.payload;
      if (state.sheets[id] === undefined) {
        console.log(`*** Sheet not found: ${id}`);
      } else {
        const newData = { ...state.sheets[id], ...obj };
        state.sheets[id] = newData;
        saveData(key, id, newData);
      }
    },
    deleteSheet(state: SheetDbState, action: PayloadAction<SheetUpdatePayload>) {
      const id = action.payload.id;
      delete state.sheets[id];
      deleteData(key, id);
    },
    saveSheets(state: SheetDbState, action: PayloadAction<void>) {
      saveAllData(key, state.sheets);
    },
    setDefaultSheet(state: SheetDbState, action: PayloadAction<string | null>) {
      state.currentSheetId = action.payload;
    },
    signUpSheets(state: SheetDbState, action: PayloadAction<SignUpPayload>) {
      const values = rewriteUserId(state.sheets, action.payload);
      addDatasToSave(key, values);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadDbModel(key).fulfilled, (state: SheetDbState, action) => {
      const values = action.payload;
      const [changed, updatedValues] = updateHashProps(initial, values);
      state.sheets = updatedValues;
      if (changed) saveAllData(key, state.sheets);
    })
  }
})

export const { initializeSheets, addSheet, updateSheet, deleteSheet, saveSheets, setDefaultSheet, signUpSheets } = sheetDbSlice.actions;
export default sheetDbSlice.reducer;
